import { ICarouselCreativeFormValuesModel } from '@/editor/shared/components/forms/creatives/models/carousel-creative-form-values';
import { IDynamicCreativeFormValuesModel } from '@/editor/shared/components/forms/creatives/models/dynamic-creative-form-values.model';
import { ISingleCreativeFormValuesModel } from '@/editor/shared/components/forms/creatives/models/single-creative-form-values.model';
import { IVideoCreativeFormValuesModel } from '@/editor/shared/components/forms/creatives/models/video-creative-form.values.model';
import { AvailablePlatformsEnum, PlatformType } from '@/editor/shared/constants/available-platforms';
import CreativeTypesEnum, { CreativeType } from '@/editor/shared/constants/creatives-types.enum';
import { ICreativeFormsState } from '@/editor/store/reducers/creative-step.slice';
import { ExternalCompanyIdEnum } from '@/shared/constants/external-company-id.enum';
import { IBaseCreativeModel } from '@/shared/models/creatives/base-creative.model';
import { isCompanyQualifiedForBrokerService } from '@/editor/shared/services/companyService';
import toast from 'react-hot-toast';
import { getValueFromLs } from '@/shared/services/localstorage';
import { EDITOR_LS_CONSTANTS } from '@/editor/shared/constants/editor-LS.constants';

export interface MutationHandler<TRequest, TResponse> {
  (data: TRequest): {
    unwrap: () => Promise<TResponse>;
  };
}

export class CreateCreativesService {
  constructor(
    private t: (key: string) => string,
    private companyExternalId: ExternalCompanyIdEnum,
    private mutations?: {
      createSingleCreativesBatch?: MutationHandler<any, any>;
      createDynamicCreativesBatch?: MutationHandler<any, any>;
      createCarouselCreativesBatch?: MutationHandler<any, any>;
      createVideoCreativeBatch?: MutationHandler<any, any>;
      brokerMutation?: MutationHandler<any, any>;
    },
    private isRegularTemplateData?: boolean
  ) {}

  public create = async (
    platformList: PlatformType[],
    selectedCreativeType: CreativeType,
    creativeFormsState: ICreativeFormsState,
    mobileMediaId?: string | null,
    googleMedia?: Record<'square' | 'landscape', string> | null
  ): Promise<{ id: string; platform: string }[]> => {
    const imageOnVideo = getValueFromLs(EDITOR_LS_CONSTANTS.IMAGE_FOR_VIDEO);
    const standardFlowPlatforms = platformList.filter((el: PlatformType) =>
      selectedCreativeType === CreativeTypesEnum.VIDEO && imageOnVideo == 'video'
        ? el != AvailablePlatformsEnum.BOARDS
        : el !== AvailablePlatformsEnum.BIDTHEATRE && el !== AvailablePlatformsEnum.BOARDS
    );
    const newsPaperPlatforms = platformList.filter(
      (el: PlatformType) =>
        (el === AvailablePlatformsEnum.BIDTHEATRE &&
          (selectedCreativeType != CreativeTypesEnum.VIDEO || imageOnVideo == 'image')) ||
        el === AvailablePlatformsEnum.BOARDS
    );

    const creativeType =
      selectedCreativeType === CreativeTypesEnum.CAROUSEL ? CreativeTypesEnum.SINGLE : selectedCreativeType;
    const [standardResults, newspaperResults] = await Promise.all([
      this.createStandard(standardFlowPlatforms, selectedCreativeType, creativeFormsState, mobileMediaId, googleMedia),
      this.createNewspapers(newsPaperPlatforms, creativeType, creativeFormsState),
    ]);

    return [...standardResults, ...newspaperResults];
  };

  private createNewspapers = async (
    platformList: PlatformType[],
    selectedCreativeType: CreativeType,
    creativeFormsState: ICreativeFormsState
  ): Promise<{ id: string; platform: string }[]> => {
    if (
      !platformList.length ||
      !this.mutations?.createSingleCreativesBatch ||
      !this.mutations?.brokerMutation ||
      !this.mutations?.createDynamicCreativesBatch
    ) {
      return [];
    }

    const map = platformList?.map((platform: PlatformType) => ({
      platform: platform,
      creative: creativeFormsState[platform][selectedCreativeType]!,
    }));

    const response =
      selectedCreativeType === CreativeTypesEnum.DYNAMIC
        ? await this.handleToast(this.mutations!.createDynamicCreativesBatch!, map)
        : await this.handleToast(this.mutations!.createSingleCreativesBatch!, map);

    const bidtheatreItem = response.items.find(
      (item: { id: string; platform: string }) => item.platform === AvailablePlatformsEnum.BIDTHEATRE
    );
    if (bidtheatreItem && isCompanyQualifiedForBrokerService(this.companyExternalId)) {
      const bidtheatreId = bidtheatreItem.id;
      localStorage.setItem('bidid', bidtheatreId);
      this.handleToast(this.mutations.brokerMutation, {
        id: bidtheatreId,
        creatives: map.filter((item) => item.platform === AvailablePlatformsEnum.BIDTHEATRE),
      });
    }

    if (bidtheatreItem && !this.isRegularTemplateData && this.companyExternalId === ExternalCompanyIdEnum.EMERA_EIE) {
      const bidtheatreId = bidtheatreItem.id;
      localStorage.setItem('bidid', bidtheatreId);
      this.handleToast(this.mutations.brokerMutation, {
        id: bidtheatreId,
        creatives: map.filter((item) => item.platform === AvailablePlatformsEnum.BIDTHEATRE),
      });
    }

    const boardsItem = response.items.find(
      (item: { id: string; platform: string }) => item.platform === AvailablePlatformsEnum.BOARDS
    );

    if (boardsItem) {
      const boardsId = boardsItem.id;
      localStorage.setItem('boardsid', boardsId);

      this.handleToast(this.mutations.brokerMutation, {
        id: boardsId,
        creatives: map.filter((item) => item.platform === AvailablePlatformsEnum.BOARDS),
      });
    }

    return response.items.map((item: { id: string; platform: string }) => ({ id: item.id, platform: item.platform }));
  };

  private createStandard = async (
    platformList: PlatformType[],
    selectedCreativeType: CreativeType,
    creativeFormsState: ICreativeFormsState,
    mobileMediaId?: string | null,
    googleMedia?: Record<'square' | 'landscape', string> | null
  ): Promise<{ id: string; platform: string }[]> => {
    if (!platformList.length) return [];

    let result: IBaseCreativeModel[] = [];

    switch (selectedCreativeType) {
      case CreativeTypesEnum.SINGLE:
        if (!this.mutations?.createSingleCreativesBatch) return [];

        const singleResponse = await this.createCreativesWithSingleElement(
          selectedCreativeType,
          this.mutations.createSingleCreativesBatch,
          platformList,
          creativeFormsState,
          mobileMediaId,
          googleMedia
        );
        result = singleResponse.items;
        break;
      case CreativeTypesEnum.DYNAMIC:
        if (!this.mutations?.createDynamicCreativesBatch) return [];
        const dynamicResponse = await this.createCreativesWithMultipleElements(
          selectedCreativeType,
          platformList,
          creativeFormsState,
          this.mutations.createDynamicCreativesBatch
        );
        result = dynamicResponse.items;
        break;
      case CreativeTypesEnum.CAROUSEL:
        if (!this.mutations?.createCarouselCreativesBatch) return [];
        const carouselResponse = await this.createCreativesWithMultipleElements(
          selectedCreativeType,
          platformList,
          creativeFormsState,
          this.mutations.createCarouselCreativesBatch
        );
        result = carouselResponse.items;
        break;
      case CreativeTypesEnum.VIDEO:
        if (!this.mutations?.createVideoCreativeBatch || !this.mutations?.createSingleCreativesBatch) return [];

        const imageOnVideo = getValueFromLs(EDITOR_LS_CONSTANTS.IMAGE_FOR_VIDEO);

        // Prepare a per-platform mutation handler map
        const platformHandlerMap = platformList
          .map((platform) => {
            const creative = creativeFormsState[platform]?.[CreativeTypesEnum.VIDEO];
            if (!creative) return null;

            // Choose handler conditionally
            const handler =
              platform === AvailablePlatformsEnum.BIDTHEATRE && imageOnVideo == 'image'
                ? this.mutations?.createSingleCreativesBatch!
                : this.mutations?.createVideoCreativeBatch!;

            return {
              platform,
              creative,
              mobileMedia: platform === AvailablePlatformsEnum.META ? mobileMediaId : null,
              handler,
            };
          })
          .filter(Boolean);

        // Group by handler to avoid calling mutation one-by-one
        const groupedByHandler = platformHandlerMap.reduce<Record<string, any[]>>((acc, item) => {
          const key = item?.handler === this.mutations!.createSingleCreativesBatch ? 'single' : 'video';
          acc[key] = acc[key] || [];
          acc[key].push(item);
          return acc;
        }, {});

        // Trigger mutations per group
        const responses: IBaseCreativeModel[] = [];

        if (groupedByHandler.single) {
          const formatted = groupedByHandler.single.map(({ platform, creative, mobileMedia }) => ({
            platform,
            creative,
            mobileMedia,
          }));
          const res = await this.handleToast(this.mutations.createSingleCreativesBatch!, formatted);
          responses.push(...res.items);
        }

        if (groupedByHandler.video) {
          const formatted = groupedByHandler.video.map(({ platform, creative, mobileMedia }) => ({
            platform,
            creative,
            mobileMedia,
          }));
          const res = await this.handleToast(this.mutations.createVideoCreativeBatch!, formatted);
          responses.push(...res.items);
        }

        result = responses;
        break;
    }

    return result.map((item) => ({ id: item.id, platform: item.platform }));
  };

  private createCreativesWithMultipleElements = async <
    TCreative extends ICarouselCreativeFormValuesModel | IDynamicCreativeFormValuesModel,
    TResponse = Record<'items', IBaseCreativeModel[]>,
  >(
    creativeType: CreativeType,
    platformList: PlatformType[],
    creativeFormsState: ICreativeFormsState,
    handler: MutationHandler<Array<{ platform: PlatformType; creative: TCreative }>, TResponse>
  ): Promise<TResponse> => {
    const creativeData = platformList.map((platform: PlatformType) => ({
      platform: platform,
      creative: creativeFormsState[platform][
        creativeType as keyof (typeof creativeFormsState)[typeof platform]
      ] as TCreative,
    }));

    return await this.handleToast(handler, creativeData);
  };

  private createCreativesWithSingleElement = async <
    TCreative extends ISingleCreativeFormValuesModel | IVideoCreativeFormValuesModel,
    TResponse = Record<'items', IBaseCreativeModel[]>,
  >(
    creativeType: CreativeType,
    handler: MutationHandler<
      Array<{
        platform: PlatformType;
        creative: TCreative;
        mobileMedia?: string | null;
        googleSquareMedia?: string | null;
        googleHorizontalMedia?: string | null;
      }>,
      TResponse
    >,
    platformList: PlatformType[],
    creativeFormsState: ICreativeFormsState,
    mobileMediaId?: string | null,
    googleMedia?: Record<'square' | 'landscape', string> | null
  ): Promise<TResponse> => {
    const googleSquareMedia = googleMedia ? googleMedia['square'] : null;
    const googleHorizontalMedia = googleMedia ? googleMedia['landscape'] : null;

    const map = platformList.map((platform: PlatformType) => ({
      platform: platform,
      creative: creativeFormsState[platform][creativeType]! as TCreative,
      mobileMedia: platform === AvailablePlatformsEnum.META ? mobileMediaId : null,
      googleSquareMedia: platform === AvailablePlatformsEnum.GOOGLE_ADS ? googleSquareMedia : null,
      googleHorizontalMedia: platform === AvailablePlatformsEnum.GOOGLE_ADS ? googleHorizontalMedia : null,
    }));

    return await this.handleToast(handler, map);
  };

  private handleToast = async (handler: MutationHandler<any, any>, data: any) => {
    return await toast.promise(handler(data).unwrap(), {
      loading: this.t('creatingCreatives'),
      success: this.t('successfullyCreated'),
      error: this.t('somethingWrong'),
    });
  };
}
